import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { DjRoomPerks } from "@/components/DjRoomPerks";
import { FilmedUpsell, FilmedUpgradedNote } from "@/components/booking/FilmedUpsell";
import { FilmedVideo } from "@/components/home/FilmedSetsSection";
import { PayLaterCalculator, AfterpayToday } from "@/components/booking/PayLaterCalculator";
import { VoucherRedeem } from "@/components/booking/VoucherRedeem";
import { ChristmasVoucherBanner } from "@/components/vouchers/ChristmasVoucherBanner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Upload, Clock, Calendar as CalendarIcon, User, Mail, Phone, Music, Sparkles } from "lucide-react";
import axios from "axios";
import { trackInitiateCheckout, trackAddToCart } from "@/utils/metaPixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Services excluded from promo codes
const PROMO_EXCLUDED_SERVICES = [
  "dj_room_hire",
  "package_dj_popular",
  "package_dj_filmed",
  "package_dj_afterdark",
  "course_dj_lesson",
  "course_ableton",
  "course_ableton_alex",
];

// DJ Services that require ID upload
const DJ_ID_REQUIRED_SERVICES = [
  "dj_room_hire",
  "package_dj_popular",
  "package_dj_filmed",
  "package_dj_afterdark",
  "course_dj_lesson",
];

// Helper function to format time in 12-hour format (supports late-night hours 24-27 = 12AM-3AM next-day)
// Production courses are scheduled directly with the mentor — no date/time picker
const PRODUCTION_COURSE_TYPES = ["ableton", "ableton_alex"];
const isProductionCourse = (svc) => PRODUCTION_COURSE_TYPES.includes(svc?.courseType);

const formatTime12h = (hour, minutes = 0) => {
  const h24 = ((hour % 24) + 24) % 24;
  const h = h24 % 12 || 12;
  const period = h24 < 12 ? 'AM' : 'PM';
  if (minutes === 0) {
    return `${h}:00 ${period}`;
  }
  return `${h}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// DJ Room late-night pricing rules — must match backend
const DJ_LATE_NIGHT_HOURS = new Set([24, 25, 26, 27, 0, 1, 2, 3]);
const DJ_LATE_NIGHT_RATE = 100;
const DJ_REGULAR_RATE = 30;
const computeDjRoomHireTotal = (slots = []) =>
  slots.reduce((sum, h) => sum + (DJ_LATE_NIGHT_HOURS.has(h) ? DJ_LATE_NIGHT_RATE : DJ_REGULAR_RATE), 0);

const formatTimeRange12h = (startHour, endHour, endMinutes = 0) => {
  const startStr = formatTime12h(startHour);
  const endStr = formatTime12h(endHour, endMinutes);
  return `${startStr} - ${endStr}`;
};

const packages = [
  {
    id: "package_dmrg_studio",
    title: "DMRG Studio Experience Package",
    price: 400,
    priceDisplay: "$400",
    features: ["2 Hours Recording Time", "One Mix", "One Master"],
    popular: true,
    requiresId: false,
    type: "package",
    room: "recording_studio",
    defaultHours: 2
  },
  {
    id: "package_dj_popular",
    title: "Dream Maker Sessions Popular Package (DJ Room)",
    price: 60,
    priceDisplay: "$60",
    features: ["2 Hours Room Hire", "Free Water and Soft Drink", "Fast WiFi", "Sennheiser HD 25 Headphones Included"],
    popular: false,
    requiresId: true,
    type: "package",
    room: "dj_room",
    defaultHours: 2
  },
  {
    id: "package_dj_filmed",
    title: "Dream Maker Sessions + Filmed (Film Your Set / Content Session)",
    price: 125,
    priceDisplay: "$125",
    features: ["2 Hours DJ Room Hire", "Set Filmed on Nikon Z5 + GoPro", "Video Filmed for Socials", "High-Quality Audio Recording of Your Full Set", "Raw Clips Delivered to You", "Free Water and Soft Drink", "Sennheiser HD 25 Headphones Included"],
    popular: false,
    requiresId: true,
    type: "package",
    room: "dj_room",
    defaultHours: 2
  },
  {
    id: "package_dj_afterdark",
    title: "DMRG Afterdark Session",
    price: 100,
    priceDisplay: "$100/hr",
    features: [
      "Late-Night Studio Takeover (12 AM – 4 AM)",
      "Full DJ Room Shutdown — Just You & Your Crew",
      "3× Pioneer CDJ-3000 + DJM-A9",
      "$100/hr · Minimum 2 Hours ($200)",
    ],
    popular: false,
    requiresId: true,
    type: "hourly",
    room: "dj_room",
    defaultHours: 2,
    afterdarkOnly: true,
  },
  {
    id: "package_photography",
    title: "Photography (Press Shots)",
    price: 299.99,
    priceDisplay: "$299.99",
    features: ["30+ Edited Photos", "Professional Lighting", "Various Backdrops", "For Artists, Creatives & Business"],
    popular: false,
    requiresId: false,
    type: "package",
    room: "photography_studio",
    defaultHours: 1
  },
  {
    id: "course_ableton",
    title: "Ableton / FL Studio Production Course (with Lister)",
    price: 1250,
    priceDisplay: "$1,250",
    features: ["4 Weeks Program", "8 Hours Total (2 hrs/week)", "1-on-1 Mentorship With Lister", "All Levels Welcome"],
    popular: false,
    requiresId: false,
    type: "course",
    courseType: "ableton",
    room: "production_room",
    defaultHours: 2
  },
  {
    id: "course_ableton_alex",
    title: "Ableton Live Production Course (with Alex M · 8 Weeks)",
    price: 2499,
    priceDisplay: "$2,499",
    features: ["8 Weeks Program", "16 Hours Total (2 hrs/week)", "1-on-1 Mentorship With Alex M", "Hardstyle / Rawstyle Specialty", "Sample Packs, Templates, Presets Included", "1 Month FREE Harder Class"],
    popular: false,
    requiresId: false,
    type: "course",
    courseType: "ableton_alex",
    room: "production_room",
    defaultHours: 2
  },
  {
    id: "package_ultimate",
    title: "Ultimate DMRG Experience",
    price: 1000,
    priceDisplay: "$1000",
    features: ["2 Hours Studio Time", "One Mix", "One Master", "Artist Press Shots", "Behind The Scenes Footage / Vlog", "Artwork for Release"],
    popular: false,
    requiresId: false,
    type: "package",
    room: "recording_studio",
    defaultHours: 2
  },
  {
    id: "course_dj_lesson",
    title: "DJ Lessons",
    price: 150,
    priceDisplay: "$150",
    features: ["1.5 Hour Session", "Pioneer Equipment", "All Levels Welcome", "Flexible Scheduling"],
    popular: false,
    requiresId: true,
    type: "course",
    courseType: "dj",
    room: "dj_room",
    defaultHours: 1.5,
    sessionDuration: 1.5
  },
  // Music Lessons removed Feb 2026
];

const services = [
  { id: "recording_with_engineer", name: "Recording (With Engineer)", price: 100, type: "hourly", room: "recording_studio" },
  { id: "dj_room_hire", name: "DJ Room Hire (Dream Makers Sessions)", price: 30, type: "hourly", room: "dj_room" },
  { id: "production_suite_hire", name: "Production Suite Hire", price: 40, type: "hourly", room: "production_room" },
  { id: "recording_dry_hire", name: "Recording (Dry Hire)", price: 90, type: "hourly", room: "recording_studio" },
  { id: "rehearsal_half_day", name: "Rehearsal – Half Day (4 Hours)", price: 150, type: "fixed", room: "recording_studio" },
  { id: "rehearsal_full_day", name: "Rehearsal – Full Day (8 Hours)", price: 300, type: "fixed", room: "recording_studio" },
  { id: "mixing_1_song", name: "Mixing (1 Song)", price: 400, type: "fixed", room: null },
  { id: "mixing_2_3_songs", name: "Mixing (2-3 Songs)", price: 375, type: "per_song", room: null },
  { id: "mixing_4_7_songs", name: "Mixing (4-7 Songs)", price: 350, type: "per_song", room: null },
  { id: "mixing_8_plus_songs", name: "Mixing (8+ Songs)", price: 325, type: "per_song", room: null },
  { id: "mastering", name: "Mastering", price: 100, type: "fixed", room: null },
  { id: "custom_beat_production", name: "Custom Beat Production", price: 500, type: "fixed", room: null },
  { id: "photo_press_shots", name: "Photo Press Shots (30 Edited Photos)", price: 299.99, type: "fixed", room: "photography_studio" },
  { id: "photography_dry_hire", name: "Photography (Dry Hire)", price: 80, type: "hourly", room: "photography_studio" },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

export default function Booking() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Ref for auto-scrolling to booking form
  const bookingFormRef = useRef(null);
  
  // Form state
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [idDocument, setIdDocument] = useState(null);
  
  const [formData, setFormData] = useState({
    fullName: "",
    artistName: "",
    email: "",
    phone: "",
  });
  
  // Promo code state
  const [promoCode, setPromoCode] = useState("");
  const [promoValidation, setPromoValidation] = useState(null); // null, { valid: true, discount_percent: 10 }, { valid: false, error: "..." }
  const [voucher, setVoucher] = useState(null);
  useEffect(() => setVoucher(null), [selectedService?.id]);
  const [validatingPromo, setValidatingPromo] = useState(false);

  // Course questionnaire state
  const [courseAnswers, setCourseAnswers] = useState({
    // Ableton course questions
    hasAbletonExperience: "",
    hasLaptopWithAbleton: "",
    canCommitWeekly: "",
    // DJ course questions
    djExperienceLevel: "",
    hasHeadphones: "",
    hasRekordbox: "",
    // Music lesson questions
    musicExperienceLevel: "",
    hasOwnInstrument: "",
    instrumentType: "",
  });

  // Check for course/service selection from URL params
  useEffect(() => {
    const courseParam = searchParams.get("course");
    const serviceParam = searchParams.get("service");

    // Direct service param — used by Home page "Book the favourites" CTAs and other deep-links.
    // Preselects the package and jumps to date/time step.
    if (serviceParam) {
      const pkg = packages.find(p => p.id === serviceParam);
      if (pkg) {
        setSelectedService({
          id: pkg.id,
          name: pkg.title,
          price: pkg.price,
          type: pkg.type || "fixed",
          room: pkg.room || null,
          requiresId: pkg.requiresId || false,
          courseType: pkg.courseType || null,
          defaultHours: pkg.defaultHours || 2,
          isPackage: true
        });
        setStep(isProductionCourse(pkg) ? 3 : 2);
        return;
      }
    }

    if (courseParam === "ableton") {
      const abletonCourse = packages.find(p => p.id === "course_ableton");
      if (abletonCourse) {
        setSelectedService({
          id: abletonCourse.id,
          name: abletonCourse.title,
          price: abletonCourse.price,
          type: "course",
          room: abletonCourse.room,
          requiresId: false,
          courseType: "ableton",
          defaultHours: abletonCourse.defaultHours || 2,
          isPackage: true
        });
        setStep(3);
      }
    } else if (courseParam === "ableton_alex") {
      const alexCourse = packages.find(p => p.id === "course_ableton_alex");
      if (alexCourse) {
        setSelectedService({
          id: alexCourse.id,
          name: alexCourse.title,
          price: alexCourse.price,
          type: "course",
          room: alexCourse.room,
          requiresId: false,
          courseType: "ableton_alex",
          defaultHours: alexCourse.defaultHours || 2,
          isPackage: true
        });
        setStep(3);
      }
    } else if (courseParam === "dj") {
      const djCourse = packages.find(p => p.id === "course_dj_lesson");
      if (djCourse) {
        setSelectedService({
          id: djCourse.id,
          name: djCourse.title,
          price: djCourse.price,
          type: "course",
          room: djCourse.room,
          requiresId: false,
          courseType: "dj",
          defaultHours: djCourse.defaultHours || 2,
          isPackage: true
        });
        setStep(2); // Go to date/time selection
      }
    }
  }, [searchParams]);

  // Check for cancelled payment
  useEffect(() => {
    if (searchParams.get("cancelled") === "true") {
      toast.error("Payment was cancelled. Please try again.");
    }
  }, [searchParams]);

  // Fetch availability when date and service change
  const fetchAvailability = useCallback(async () => {
    if (!selectedDate || !selectedService?.room) return;
    
    // Format date correctly without timezone issues
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    try {
      const res = await axios.get(`${API}/availability/${selectedService.room}/${dateStr}`);
      setAvailableSlots(res.data.available_slots);
    } catch (error) {
      console.error("Error fetching availability:", error);
      setAvailableSlots([]);
    }
  }, [selectedDate, selectedService]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const handlePackageSelect = (pkg) => {
    setSelectedService({
      id: pkg.id,
      name: pkg.title,
      price: pkg.price,
      type: pkg.type || "fixed",  // Preserve original type — hourly packages (e.g. Afterdark) must multiply by slots
      room: pkg.room || null,
      requiresId: pkg.requiresId || false,
      courseType: pkg.courseType || null,
      defaultHours: pkg.defaultHours || 2,
      isPackage: true
    });
    setSelectedSlots([]);
    setSelectedDate(null);
    setStep(isProductionCourse(pkg) ? 3 : 2); // Production courses skip date/time
    
    // Auto-scroll to booking form after state update
    setTimeout(() => {
      bookingFormRef.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start'
      });
    }, 100);
  };

  const handleServiceSelect = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    setSelectedService(service);
    setSelectedSlots([]);
    setSelectedDate(null);
    setQuantity(1);
    
    // Auto-scroll to show selected service and next button
    setTimeout(() => {
      bookingFormRef.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start'
      });
    }, 100);
  };

  // Packages that require 2-hour booking slots
  const TWO_HOUR_PACKAGES = [
    "package_dmrg_studio",    // DMRG Studio Experience Package
    "package_dj_popular",     // Dream Maker Sessions Popular Package
    "package_dj_filmed",      // Dream Maker Sessions + Filmed
  ];

  // Afterdark package — only allow late-night hours (12AM-4AM)
  const AFTERDARK_SERVICES = [
    "package_dj_afterdark",   // Dream Makers Sessions (Afterdark)
  ];

  // Regular DJ Room services — 12AM-4AM is only bookable as an Afterdark Session
  const DJ_DAYTIME_ONLY = ["dj_room_hire", "package_dj_popular", "package_dj_filmed"];
  const AFTERDARK_MIN_HOURS = 2;

  const switchToAfterdark = () => {
    const pkg = packages.find(p => p.id === "package_dj_afterdark");
    setSelectedService({
      id: pkg.id, name: pkg.title, price: pkg.price, type: pkg.type, room: pkg.room,
      requiresId: true, courseType: null, defaultHours: 2, isPackage: true
    });
    setSelectedSlots([]);
    setPromoValidation(null);
  };

  // 1.5 hour session packages
  const NINETY_MIN_PACKAGES = [
    "course_dj_lesson",       // DJ Lessons
  ];

  // 1 hour session packages (music lessons and photo press shots)
  const ONE_HOUR_PACKAGES = [
    "music_lesson_1hr",       // Music Lesson - 1 Hour
    "music_lesson_4week",     // Music Lessons - 4 Week Program (1hr per session)
    "photo_press_shots",      // Photo Press Shots - 1 hour session
    "package_photography",    // Photography Package - 1 hour session
  ];

  // 45 minute session packages
  const FORTYFIVE_MIN_PACKAGES = [
    "music_lesson_45min",     // Music Lesson - 45 Minutes
  ];

  // 30 minute session packages
  const THIRTY_MIN_PACKAGES = [
    "music_lesson_30min",     // Music Lesson - 30 Minutes
  ];

  const handleSlotToggle = (hour) => {
    const isTwoHourPackage = selectedService && TWO_HOUR_PACKAGES.includes(selectedService.id);
    const isAfterdark = selectedService && AFTERDARK_SERVICES.includes(selectedService.id);
    const isNinetyMinPackage = selectedService && NINETY_MIN_PACKAGES.includes(selectedService.id);
    const isOneHourPackage = selectedService && ONE_HOUR_PACKAGES.includes(selectedService.id);
    const isFortyFiveMinPackage = selectedService && FORTYFIVE_MIN_PACKAGES.includes(selectedService.id);
    const isThirtyMinPackage = selectedService && THIRTY_MIN_PACKAGES.includes(selectedService.id);
    
    // For music lesson packages (30min, 45min, 1hr), select single starting hour
    if (isThirtyMinPackage || isFortyFiveMinPackage || isOneHourPackage) {
      setSelectedSlots(prev => {
        if (prev.includes(hour)) {
          return [];
        }
        return [hour];
      });
    } else if (isNinetyMinPackage) {
      // For 1.5-hour packages, select the starting hour (backend will handle 1.5hr duration)
      setSelectedSlots(prev => {
        if (prev.includes(hour)) {
          return [];
        }
        // Check if next hour is available (we need at least partial availability)
        const nextHourSlot = availableSlots.find(s => s.hour === hour + 1);
        if (!nextHourSlot || !nextHourSlot.available) {
          toast.error("This time slot requires 1.5 hours. The following time is not available.");
          return prev;
        }
        // Store just the start hour, but mark it as 1.5hr session
        return [hour];
      });
    } else if (isTwoHourPackage) {
      // For 2-hour packages, select exactly 2 consecutive hours
      setSelectedSlots(prev => {
        // If clicking on already selected slot, deselect both
        if (prev.includes(hour)) {
          return [];
        }
        
        // Check if next hour is available
        const nextHourSlot = availableSlots.find(s => s.hour === hour + 1);
        if (!nextHourSlot || !nextHourSlot.available) {
          toast.error("This time slot requires 2 consecutive hours. The next hour is not available.");
          return prev;
        }
        
        // Select this hour and the next hour (2-hour block)
        return [hour, hour + 1];
      });
    } else {
      // For hourly services, allow individual slot selection
      setSelectedSlots(prev => {
        if (prev.includes(hour)) {
          return prev.filter(h => h !== hour);
        }
        // Check if slot is adjacent to maintain consecutive selection
        const sorted = [...prev, hour].sort((a, b) => a - b);
        const isConsecutive = sorted.every((val, idx, arr) => 
          idx === 0 || val === arr[idx - 1] + 1
        );
        if (prev.length > 0 && !isConsecutive) {
          toast.error("Please select consecutive time slots");
          return prev;
        }
        return sorted;
      });
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setIdDocument(reader.result);
        toast.success("ID uploaded successfully");
      };
      reader.readAsDataURL(file);
    }
  };

  const calculateTotal = () => {
    if (!selectedService) return 0;
    if (selectedService.id === "dj_room_hire") {
      return computeDjRoomHireTotal(selectedSlots);
    }
    if (selectedService.type === "hourly") {
      return selectedService.price * selectedSlots.length;
    }
    if (selectedService.type === "per_song") {
      return selectedService.price * quantity;
    }
    return selectedService.price;
  };

  const handleNext = () => {
    if (step === 1 && !selectedService) {
      toast.error("Please select a service");
      return;
    }
    if (step === 2) {
      // Require date for all services/packages with a room
      if (selectedService.room && !selectedDate) {
        toast.error("Please select a date");
        return;
      }
      // Require time slots for hourly services OR packages
      if (selectedService.room && selectedSlots.length === 0) {
        toast.error("Please select at least one time slot");
        return;
      }
      if (AFTERDARK_SERVICES.includes(selectedService.id) && selectedSlots.length < AFTERDARK_MIN_HOURS) {
        toast.error("Afterdark Sessions have a 2-hour minimum ($200)");
        return;
      }
    }
    if (step === 3) {
      if (!formData.fullName || !formData.email || !formData.phone) {
        toast.error("Please fill in all required fields");
        return;
      }
      if ((DJ_ID_REQUIRED_SERVICES.includes(selectedService.id) || selectedService.requiresId) && !idDocument) {
        toast.error("ID upload is required for this booking");
        return;
      }
      // Validate Ableton course questionnaire
      if (selectedService?.courseType === "ableton") {
        if (!courseAnswers.hasAbletonExperience || !courseAnswers.hasLaptopWithAbleton || !courseAnswers.canCommitWeekly) {
          toast.error("Please answer all course questionnaire questions");
          return;
        }
      }
      // Validate DJ course questionnaire
      if (selectedService?.courseType === "dj") {
        if (!courseAnswers.djExperienceLevel || !courseAnswers.hasHeadphones || !courseAnswers.hasRekordbox) {
          toast.error("Please answer all course questionnaire questions");
          return;
        }
      }
      // Validate Music Lesson questionnaire
      if (selectedService?.courseType === "music_lesson") {
        if (!courseAnswers.musicExperienceLevel || !courseAnswers.hasOwnInstrument || !courseAnswers.instrumentType) {
          toast.error("Please answer all lesson questions");
          return;
        }
      }
    }
    setStep(step + 1);
  };

  const swapPackage = (toId) => {
    const pkg = packages.find(p => p.id === toId);
    setSelectedService((prev) => ({ ...prev, id: pkg.id, name: pkg.title, price: pkg.price, upgradedFrom: toId === "package_dj_filmed" ? prev.id : undefined }));
    setPromoValidation(null);
  };

  const handleBack = () => {
    if (step === 3 && isProductionCourse(selectedService)) {
      setStep(1);
      return;
    }
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      // Format date correctly without timezone issues
      const formatDateLocal = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      // Create booking
      const bookingData = {
        full_name: formData.fullName,
        artist_name: formData.artistName || null,
        email: formData.email,
        phone: formData.phone,
        service_id: selectedService.id,
        booking_date: selectedDate ? formatDateLocal(selectedDate) : formatDateLocal(new Date()),
        time_slots: selectedSlots.length > 0 ? selectedSlots : [],
        quantity: quantity,
        id_document: idDocument,
      };
      
      // Add music lesson specific fields
      if (selectedService?.courseType === "music_lesson") {
        bookingData.experience_level = courseAnswers.musicExperienceLevel;
        bookingData.has_own_instrument = courseAnswers.hasOwnInstrument;
        bookingData.instrument_type = courseAnswers.instrumentType;
      }

      const bookingRes = await axios.post(`${API}/bookings`, bookingData);
      const bookingId = bookingRes.data.id;

      // Create checkout session with promo code if valid
      const checkoutData = {
        booking_id: bookingId,
        origin_url: window.location.origin,
      };
      
      // Only include promo code if it's valid and service is eligible
      if (promoValidation?.valid && promoCode && !PROMO_EXCLUDED_SERVICES.includes(selectedService.id)) {
        checkoutData.promo_code = promoCode.toUpperCase().trim();
      }
      if (voucher) checkoutData.voucher_code = voucher.code;
      
      // Track checkout initiation with Meta Pixel
      trackInitiateCheckout(
        selectedService.price * (selectedSlots.length || quantity || 1),
        'AUD',
        [selectedService.id]
      );
      
      const checkoutRes = await axios.post(`${API}/checkout/create`, checkoutData);

      // Redirect to payment
      window.location.href = checkoutRes.data.url;
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(error.response?.data?.detail || "Failed to create booking. Please try again.");
      setLoading(false);
    }
  };
  
  // Validate promo code
  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoValidation(null);
      return;
    }
    
    if (!formData.email) {
      toast.error("Please enter your email first to validate promo code");
      return;
    }
    
    if (PROMO_EXCLUDED_SERVICES.includes(selectedService?.id)) {
      setPromoValidation({ valid: false, error: "Promo codes cannot be applied to DJ services or courses." });
      return;
    }
    
    setValidatingPromo(true);
    try {
      const res = await axios.post(`${API}/promo/validate`, null, {
        params: {
          code: promoCode.toUpperCase().trim(),
          service_id: selectedService?.id,
          customer_email: formData.email
        }
      });
      setPromoValidation(res.data);
      if (res.data.valid) {
        toast.success(`Promo code applied! ${res.data.discount_percent}% discount`);
      } else {
        toast.error(res.data.error || "Invalid promo code");
      }
    } catch (error) {
      console.error("Promo validation error:", error);
      setPromoValidation({ valid: false, error: "Could not validate promo code" });
      toast.error("Could not validate promo code");
    } finally {
      setValidatingPromo(false);
    }
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  return (
    <div className="pt-20 min-h-screen bg-dream-bg overflow-x-hidden" data-testid="booking-page">
      {/* Hero */}
      <section className="py-16 bg-dream-bg">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <p className="text-dream-violet font-mono text-sm tracking-widest mb-4 uppercase">Book Your Session</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
              Reserve Your Studio Time
            </h1>
            <p className="text-white/60">
              Select your service, choose your time, and pay securely online.
            </p>
          </motion.div>
          <ChristmasVoucherBanner scope="all" className="rounded-2xl overflow-hidden mb-12 [&>div]:px-5" />

          {/* Popular Packages */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ delay: 0.1 }}
            className="mb-12"
          >
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-5 h-5 text-dream-violet" />
              <h2 className="font-heading text-xl font-bold text-white">Popular Packages & Courses</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {packages.map((pkg, index) => (
                <div
                  key={pkg.title}
                  className={`relative glow-card p-6 rounded-xl cursor-pointer transition-all hover:scale-[1.02] ${pkg.popular ? 'border-dream-violet' : ''} ${pkg.type === 'course' ? 'border-cyan-500/50' : ''}`}
                  data-testid={`booking-package-${index}`}
                  onClick={() => handlePackageSelect(pkg)}
                >
                  {pkg.popular && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-dream-violet text-white text-xs font-bold px-3 py-0.5 rounded-full">
                      MOST POPULAR
                    </div>
                  )}
                  {pkg.type === 'course' && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-cyan-500 text-white text-xs font-bold px-3 py-0.5 rounded-full">
                      COURSE
                    </div>
                  )}
                  {pkg.id === "package_dj_filmed" && (
                    <div className="relative -mx-6 -mt-6 mb-4 h-72 overflow-hidden rounded-t-xl" data-testid="booking-filmed-video">
                      <FilmedVideo testId="booking-filmed-video-el" className="object-top" />
                      <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/60 text-[10px] font-bold text-white tracking-widest"><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />REC</span>
                      <span className="absolute top-3 right-3 bg-fuchsia-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">NEW</span>
                    </div>
                  )}
                  <h3 className="font-heading text-lg font-bold text-white mb-1 mt-1">{pkg.title}</h3>
                  <div className="mb-4">
                    <span className={`font-mono text-2xl font-bold ${pkg.type === 'course' ? 'text-cyan-400' : 'text-dream-violet'}`}>{pkg.priceDisplay}</span>
                    {!pkg.priceDisplay.includes("/") && <AfterpayToday price={pkg.price} className="mt-1" testId={`booking-package-afterpay-${pkg.id}`} />}
                  </div>
                  <ul className="space-y-2 mb-4">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${pkg.type === 'course' ? 'bg-cyan-400' : 'bg-dream-violet'}`} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full rounded-full text-sm ${pkg.popular ? 'bg-dream-violet text-white hover:bg-dream-violet-glow' : pkg.type === 'course' ? 'bg-cyan-500 text-white hover:bg-cyan-400' : 'bg-white/10 text-white hover:bg-white/20'}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePackageSelect(pkg);
                    }}
                  >
                    {pkg.type === 'course' ? 'Enrol Now' : 'Book Now'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-12">
            <div className="flex-1 h-px bg-dream-border" />
            <span className="text-white/40 text-sm">or book individual services</span>
            <div className="flex-1 h-px bg-dream-border" />
          </div>

          {/* Progress Steps */}
          <div className="flex justify-center mb-12">
            <div className="flex items-center gap-4">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className="flex items-center gap-4">
                  <div 
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-mono font-bold transition-colors ${
                      step >= s ? "bg-dream-violet text-white" : "bg-dream-surface text-white/50 border border-dream-border"
                    }`}
                  >
                    {s}
                  </div>
                  {s < 4 && (
                    <div className={`w-12 h-0.5 ${step > s ? "bg-dream-violet" : "bg-dream-border"}`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div ref={bookingFormRef} className="glass rounded-2xl p-8 scroll-mt-24">
            {/* Step 1: Select Service */}
            {step === 1 && (
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                data-testid="step-1"
              >
                <h2 className="font-heading text-2xl font-bold text-white mb-6">Select a Service</h2>
                <div className="grid gap-3 max-h-[400px] overflow-y-auto pr-2">
                  {services.map((service) => (
                    <button
                      key={service.id}
                      onClick={() => handleServiceSelect(service.id)}
                      data-testid={`service-option-${service.id}`}
                      className={`p-4 rounded-xl text-left transition-all border ${
                        selectedService?.id === service.id
                          ? "bg-dream-violet/20 border-dream-violet"
                          : "bg-dream-surface border-dream-border hover:border-dream-violet/50"
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-white">{service.name}</span>
                        <span className="font-mono text-dream-violet">
                          ${service.price}
                          <span className="text-white/50 text-sm">
                            {service.type === "hourly" ? "/hr" : service.type === "per_song" ? "/song" : ""}
                          </span>
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 2: Select Date & Time */}
            {step === 2 && (
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                data-testid="step-2"
              >
                <h2 className="font-heading text-2xl font-bold text-white mb-2">
                  {selectedService?.room ? "Select Date & Time" : "Confirm Details"}
                </h2>
                {selectedService?.isPackage && (
                  <p className="text-dream-violet mb-6">
                    {selectedService.name} - ${selectedService.price.toLocaleString('en-AU')}{selectedService.type === 'hourly' ? '/hr' : ''}
                  </p>
                )}
                
                {selectedService?.room === "dj_room" && <DjRoomPerks className="mb-8" testId="booking-dj-room-perks" stacked />}
                {selectedService?.room ? (
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Calendar */}
                    <div>
                      <Label className="text-white/60 mb-2 flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4" /> Select Date
                      </Label>
                      <div className="bg-dream-surface rounded-xl p-4 border border-dream-border">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={setSelectedDate}
                          disabled={isDateDisabled}
                          className="mx-auto"
                          data-testid="booking-calendar"
                        />
                      </div>
                    </div>

                    {/* Time Slots */}
                    <div>
                      <Label className="text-white/60 mb-2 flex items-center gap-2">
                        <Clock className="w-4 h-4" /> Select Time Slot
                        {TWO_HOUR_PACKAGES.includes(selectedService?.id) && (
                          <span className="text-dream-violet text-xs font-medium">(2-hour session)</span>
                        )}
                        {NINETY_MIN_PACKAGES.includes(selectedService?.id) && (
                          <span className="text-dream-violet text-xs font-medium">(1.5-hour session)</span>
                        )}
                      </Label>
                      {TWO_HOUR_PACKAGES.includes(selectedService?.id) && (
                        <p className="text-white/50 text-xs mb-3">
                          Click on a start time to book a 2-hour block
                        </p>
                      )}
                      {NINETY_MIN_PACKAGES.includes(selectedService?.id) && (
                        <p className="text-white/50 text-xs mb-3">
                          Click on a start time to book a 1.5-hour session
                        </p>
                      )}
                      {AFTERDARK_SERVICES.includes(selectedService?.id) && (
                        <div className="mb-4 p-3 rounded-lg bg-purple-500/10 border border-purple-500/40">
                          <p className="text-purple-300 text-xs font-mono uppercase tracking-widest mb-1">DMRG Afterdark Session</p>
                          <p className="text-white/80 text-xs">Full studio shutdown 12 AM – 4 AM. Pick 2–4 consecutive hours at <b className="text-purple-300">$100/hr</b> (minimum $200). ID required at door.</p>
                        </div>
                      )}

                      {selectedDate ? (
                        availableSlots.length > 0 ? (
                          <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                            {availableSlots
                              .filter((slot) => {
                                // Afterdark: only show late-night slots (24-27 = 12AM-3AM)
                                if (AFTERDARK_SERVICES.includes(selectedService?.id)) {
                                  return DJ_LATE_NIGHT_HOURS.has(slot.hour);
                                }
                                if (DJ_DAYTIME_ONLY.includes(selectedService?.id)) {
                                  return !DJ_LATE_NIGHT_HOURS.has(slot.hour) && !(TWO_HOUR_PACKAGES.includes(selectedService.id) && slot.hour === 23);
                                }
                                return true;
                              })
                              .map((slot) => {
                              return (
                                <button
                                  key={slot.hour}
                                  onClick={() => slot.available && handleSlotToggle(slot.hour)}
                                  disabled={!slot.available}
                                  data-testid={`time-slot-${slot.hour}`}
                                  className={`time-slot p-3 rounded-lg text-sm font-mono border transition-all relative ${
                                    selectedSlots.includes(slot.hour)
                                      ? "selected"
                                      : slot.available
                                      ? "bg-dream-surface border-dream-border text-white hover:border-dream-violet"
                                      : "bg-red-900/20 border-red-900/30 text-white/30 cursor-not-allowed"
                                  }`}
                                >
                                  {slot.display}
                                  {!slot.available && (
                                    <span className="block text-xs text-red-400 mt-1">
                                      {slot.too_soon ? "TOO SOON (1hr notice)" : "BOOKED"}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-white/50 text-center py-8">No available slots for this date</p>
                        )
                      ) : (
                        <p className="text-white/50 text-center py-8">Please select a date first</p>
                      )}
                      {DJ_DAYTIME_ONLY.includes(selectedService?.id) && (
                        <button
                          type="button"
                          onClick={switchToAfterdark}
                          className="mt-4 w-full text-left p-4 rounded-xl border border-purple-500/50 bg-gradient-to-r from-purple-900/40 to-indigo-900/30 hover:border-purple-400 transition-colors group"
                          data-testid="afterdark-switch-btn"
                        >
                          <span className="inline-block -rotate-2 px-2.5 py-1 rounded-full bg-purple-500 text-white text-[10px] font-bold uppercase tracking-widest mb-2">After 12 AM</span>
                          <span className="block text-white font-semibold">Going past midnight? Book a DMRG Afterdark Session</span>
                          <span className="block text-white/60 text-xs mt-1">12 AM – 4 AM · $100/hr · minimum 2 hours ($200) · full DJ room shutdown</span>
                          <span className="block text-purple-300 text-xs font-semibold mt-2 group-hover:underline">Switch to Afterdark →</span>
                        </button>
                      )}
                      {selectedSlots.length > 0 && (
                        <p className="text-dream-violet text-sm mt-4">
                          {THIRTY_MIN_PACKAGES.includes(selectedService?.id) ? (
                            <span>30 min session • {formatTime12h(selectedSlots[0])} - {formatTime12h(selectedSlots[0], 30)}</span>
                          ) : FORTYFIVE_MIN_PACKAGES.includes(selectedService?.id) ? (
                            <span>45 min session • {formatTime12h(selectedSlots[0])} - {formatTime12h(selectedSlots[0], 45)}</span>
                          ) : ONE_HOUR_PACKAGES.includes(selectedService?.id) ? (
                            <span>1 hour session • {formatTimeRange12h(selectedSlots[0], selectedSlots[0] + 1)}</span>
                          ) : NINETY_MIN_PACKAGES.includes(selectedService?.id) ? (
                            <span>1.5 hours selected • {formatTimeRange12h(selectedSlots[0], selectedSlots[0] + 1, 30)}</span>
                          ) : selectedSlots.length >= 2 ? (
                            <span>{selectedSlots.length} hours selected • {formatTimeRange12h(selectedSlots[0], selectedSlots[selectedSlots.length - 1] + 1)}</span>
                          ) : (
                            <span>{selectedSlots.length} hour(s) selected • {formatTime12h(selectedSlots[0])} - {formatTime12h(selectedSlots[selectedSlots.length - 1] + 1)}</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                ) : selectedService?.type === "per_song" ? (
                  <div className="max-w-xs">
                    <Label className="text-white/60 mb-2 flex items-center gap-2">
                      <Music className="w-4 h-4" /> Number of Songs
                    </Label>
                    <Select value={quantity.toString()} onValueChange={(v) => setQuantity(parseInt(v))}>
                      <SelectTrigger className="bg-dream-surface border-dream-border text-white" data-testid="quantity-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-dream-surface border-dream-border">
                        {[...Array(10)].map((_, i) => (
                          <SelectItem key={i + 1} value={(i + 1).toString()} className="text-white">
                            {i + 1} song{i > 0 ? "s" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-white/60 mb-2">Selected Service:</p>
                    <p className="font-heading text-xl text-white">{selectedService?.name}</p>
                    <p className="font-mono text-2xl text-dream-violet mt-2">${selectedService?.price}</p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 3: Personal Details */}
            {step === 3 && (
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                data-testid="step-3"
              >
                <h2 className="font-heading text-2xl font-bold text-white mb-6">Your Details</h2>
                {isProductionCourse(selectedService) && (
                  <div className="mb-6 p-4 rounded-xl border border-dream-violet/40 bg-dream-violet/10 max-w-md" data-testid="course-schedule-note">
                    <p className="text-white text-sm font-semibold">No need to pick dates</p>
                    <p className="text-white/70 text-sm mt-1">Once you enrol, your mentor will contact you within 24 hours to lock in your weekly session times.</p>
                  </div>
                )}
                <div className="grid gap-6 max-w-md">
                  <div>
                    <Label className="text-white/60 mb-2 flex items-center gap-2">
                      <User className="w-4 h-4" /> Full Name *
                    </Label>
                    <Input
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      placeholder="John Smith"
                      className="bg-dream-surface border-dream-border text-white placeholder:text-white/30"
                      data-testid="input-fullname"
                    />
                  </div>
                  
                  {selectedService?.type !== "course" && (
                    <div>
                      <Label className="text-white/60 mb-2 flex items-center gap-2">
                        <Music className="w-4 h-4" /> Artist Name (optional)
                      </Label>
                      <Input
                        value={formData.artistName}
                        onChange={(e) => setFormData({ ...formData, artistName: e.target.value })}
                        placeholder="Stage name"
                        className="bg-dream-surface border-dream-border text-white placeholder:text-white/30"
                        data-testid="input-artistname"
                      />
                    </div>
                  )}
                  
                  <div>
                    <Label className="text-white/60 mb-2 flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email Address *
                    </Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      className="bg-dream-surface border-dream-border text-white placeholder:text-white/30"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label className="text-white/60 mb-2 flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Mobile Number *
                    </Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="0400 000 000"
                      className="bg-dream-surface border-dream-border text-white placeholder:text-white/30"
                      data-testid="input-phone"
                    />
                  </div>
                  
                  {/* Ableton Course Questionnaire */}
                  {selectedService?.courseType === "ableton" && (
                    <>
                      <div className="border-t border-dream-border pt-6 mt-2">
                        <h3 className="font-heading text-lg font-bold text-white mb-4">Course Questionnaire</h3>
                      </div>
                      <div>
                        <Label className="text-white/60 mb-2 block">
                          Do you have Ableton Live experience? *
                        </Label>
                        <div className="flex gap-4">
                          <button
                            type="button"
                            onClick={() => setCourseAnswers({ ...courseAnswers, hasAbletonExperience: "yes" })}
                            className={`flex-1 py-3 px-4 rounded-lg border transition-all ${courseAnswers.hasAbletonExperience === "yes" ? "border-dream-violet bg-dream-violet/20 text-white" : "border-dream-border bg-dream-surface text-white/60 hover:border-dream-violet/50"}`}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => setCourseAnswers({ ...courseAnswers, hasAbletonExperience: "no" })}
                            className={`flex-1 py-3 px-4 rounded-lg border transition-all ${courseAnswers.hasAbletonExperience === "no" ? "border-dream-violet bg-dream-violet/20 text-white" : "border-dream-border bg-dream-surface text-white/60 hover:border-dream-violet/50"}`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-white/60 mb-2 block">
                          Do you have a laptop with Ableton Live installed? *
                        </Label>
                        <div className="flex gap-4">
                          <button
                            type="button"
                            onClick={() => setCourseAnswers({ ...courseAnswers, hasLaptopWithAbleton: "yes" })}
                            className={`flex-1 py-3 px-4 rounded-lg border transition-all ${courseAnswers.hasLaptopWithAbleton === "yes" ? "border-dream-violet bg-dream-violet/20 text-white" : "border-dream-border bg-dream-surface text-white/60 hover:border-dream-violet/50"}`}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => setCourseAnswers({ ...courseAnswers, hasLaptopWithAbleton: "no" })}
                            className={`flex-1 py-3 px-4 rounded-lg border transition-all ${courseAnswers.hasLaptopWithAbleton === "no" ? "border-dream-violet bg-dream-violet/20 text-white" : "border-dream-border bg-dream-surface text-white/60 hover:border-dream-violet/50"}`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-white/60 mb-2 block">
                          Can you commit to 1.5 hours per week in person at Dream Makers Recording Group HQ? *
                        </Label>
                        <div className="flex gap-4">
                          <button
                            type="button"
                            onClick={() => setCourseAnswers({ ...courseAnswers, canCommitWeekly: "yes" })}
                            className={`flex-1 py-3 px-4 rounded-lg border transition-all ${courseAnswers.canCommitWeekly === "yes" ? "border-dream-violet bg-dream-violet/20 text-white" : "border-dream-border bg-dream-surface text-white/60 hover:border-dream-violet/50"}`}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => setCourseAnswers({ ...courseAnswers, canCommitWeekly: "no" })}
                            className={`flex-1 py-3 px-4 rounded-lg border transition-all ${courseAnswers.canCommitWeekly === "no" ? "border-dream-violet bg-dream-violet/20 text-white" : "border-dream-border bg-dream-surface text-white/60 hover:border-dream-violet/50"}`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* DJ Lessons Questionnaire */}
                  {selectedService?.courseType === "dj" && (
                    <>
                      <div className="border-t border-dream-border pt-6 mt-2">
                        <h3 className="font-heading text-lg font-bold text-white mb-4">Course Questionnaire</h3>
                      </div>
                      <div>
                        <Label className="text-white/60 mb-2 block">
                          What is your DJ experience level? *
                        </Label>
                        <div className="grid gap-2">
                          {[
                            { value: "none", label: "No experience" },
                            { value: "some", label: "Know some things" },
                            { value: "experienced", label: "Experienced and want to excel my knowledge" }
                          ].map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => setCourseAnswers({ ...courseAnswers, djExperienceLevel: option.value })}
                              className={`w-full py-3 px-4 rounded-lg border text-left transition-all ${courseAnswers.djExperienceLevel === option.value ? "border-cyan-500 bg-cyan-500/20 text-white" : "border-dream-border bg-dream-surface text-white/60 hover:border-cyan-500/50"}`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-white/60 mb-2 block">
                          Do you have headphones? *
                        </Label>
                        <div className="flex gap-4">
                          <button
                            type="button"
                            onClick={() => setCourseAnswers({ ...courseAnswers, hasHeadphones: "yes" })}
                            className={`flex-1 py-3 px-4 rounded-lg border transition-all ${courseAnswers.hasHeadphones === "yes" ? "border-cyan-500 bg-cyan-500/20 text-white" : "border-dream-border bg-dream-surface text-white/60 hover:border-cyan-500/50"}`}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => setCourseAnswers({ ...courseAnswers, hasHeadphones: "no" })}
                            className={`flex-1 py-3 px-4 rounded-lg border transition-all ${courseAnswers.hasHeadphones === "no" ? "border-cyan-500 bg-cyan-500/20 text-white" : "border-dream-border bg-dream-surface text-white/60 hover:border-cyan-500/50"}`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-white/60 mb-2 block">
                          Do you have a laptop or computer with Rekordbox installed to upload music to USB or hard drive? *
                        </Label>
                        <div className="flex gap-4">
                          <button
                            type="button"
                            onClick={() => setCourseAnswers({ ...courseAnswers, hasRekordbox: "yes" })}
                            className={`flex-1 py-3 px-4 rounded-lg border transition-all ${courseAnswers.hasRekordbox === "yes" ? "border-cyan-500 bg-cyan-500/20 text-white" : "border-dream-border bg-dream-surface text-white/60 hover:border-cyan-500/50"}`}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => setCourseAnswers({ ...courseAnswers, hasRekordbox: "no" })}
                            className={`flex-1 py-3 px-4 rounded-lg border transition-all ${courseAnswers.hasRekordbox === "no" ? "border-cyan-500 bg-cyan-500/20 text-white" : "border-dream-border bg-dream-surface text-white/60 hover:border-cyan-500/50"}`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Music Lessons Questionnaire (Guitar, Bass, Drums) */}
                  {selectedService?.courseType === "music_lesson" && (
                    <>
                      <div className="border-t border-dream-border pt-6 mt-2">
                        <h3 className="font-heading text-lg font-bold text-white mb-4">Lesson Information</h3>
                      </div>
                      <div>
                        <Label className="text-white/60 mb-2 block">
                          What instrument would you like to learn? *
                        </Label>
                        <div className="grid gap-2">
                          {[
                            { value: "guitar", label: "🎸 Guitar" },
                            { value: "bass", label: "🎸 Bass" },
                            { value: "drums", label: "🥁 Drums" }
                          ].map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => setCourseAnswers({ ...courseAnswers, instrumentType: option.value })}
                              className={`w-full py-3 px-4 rounded-lg border text-left transition-all ${courseAnswers.instrumentType === option.value ? "border-dream-violet bg-dream-violet/20 text-white" : "border-dream-border bg-dream-surface text-white/60 hover:border-dream-violet/50"}`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-white/60 mb-2 block">
                          What is your experience level? *
                        </Label>
                        <div className="grid gap-2">
                          {[
                            { value: "beginner", label: "Beginner - New to the instrument" },
                            { value: "intermediate", label: "Intermediate - Know the basics" },
                            { value: "advanced", label: "Advanced - Looking to improve technique" }
                          ].map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => setCourseAnswers({ ...courseAnswers, musicExperienceLevel: option.value })}
                              className={`w-full py-3 px-4 rounded-lg border text-left transition-all ${courseAnswers.musicExperienceLevel === option.value ? "border-dream-violet bg-dream-violet/20 text-white" : "border-dream-border bg-dream-surface text-white/60 hover:border-dream-violet/50"}`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-white/60 mb-2 block">
                          Do you have your own instrument? *
                        </Label>
                        <div className="flex gap-4">
                          <button
                            type="button"
                            onClick={() => setCourseAnswers({ ...courseAnswers, hasOwnInstrument: "yes" })}
                            className={`flex-1 py-3 px-4 rounded-lg border transition-all ${courseAnswers.hasOwnInstrument === "yes" ? "border-dream-violet bg-dream-violet/20 text-white" : "border-dream-border bg-dream-surface text-white/60 hover:border-dream-violet/50"}`}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => setCourseAnswers({ ...courseAnswers, hasOwnInstrument: "no" })}
                            className={`flex-1 py-3 px-4 rounded-lg border transition-all ${courseAnswers.hasOwnInstrument === "no" ? "border-dream-violet bg-dream-violet/20 text-white" : "border-dream-border bg-dream-surface text-white/60 hover:border-dream-violet/50"}`}
                          >
                            No
                          </button>
                        </div>
                        <p className="text-white/40 text-xs mt-2">No worries if you don&apos;t - instruments are provided during lessons!</p>
                      </div>
                    </>
                  )}
                  
                  {(DJ_ID_REQUIRED_SERVICES.includes(selectedService?.id) || selectedService?.requiresId) && (
                    <div>
                      <Label className="text-white/60 mb-2 flex items-center gap-2">
                        <Upload className="w-4 h-4" /> ID Upload (Required) *
                      </Label>
                      <div className="border-2 border-dashed border-dream-border rounded-lg p-6 text-center hover:border-dream-violet/50 transition-colors">
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="id-upload"
                          data-testid="input-id-upload"
                        />
                        <label htmlFor="id-upload" className="cursor-pointer">
                          {idDocument ? (
                            <p className="text-dream-violet">ID Uploaded ✓</p>
                          ) : (
                            <>
                              <Upload className="w-8 h-8 mx-auto text-white/30 mb-2" />
                              <p className="text-white/50 text-sm">Click to upload your ID</p>
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 4: Review & Pay */}
            {step === 4 && (
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                data-testid="step-4"
              >
                <h2 className="font-heading text-2xl font-bold text-white mb-6">Review & Pay</h2>
                {selectedService?.id === "package_dj_popular" && <FilmedUpsell onUpgrade={() => swapPackage("package_dj_filmed")} />}
                {selectedService?.id === "package_dj_filmed" && selectedService?.upgradedFrom !== undefined && (
                  <FilmedUpgradedNote onRevert={() => swapPackage("package_dj_popular")} />
                )}
                <div className="bg-dream-surface rounded-xl p-6 border border-dream-border mb-6">
                  <h3 className="font-semibold text-white mb-4">Booking Summary</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/60">Service</span>
                      <span className="text-white">{selectedService?.name}</span>
                    </div>
                    {isProductionCourse(selectedService) && (
                      <div className="flex justify-between" data-testid="review-schedule-row">
                        <span className="text-white/60">Schedule</span>
                        <span className="text-white">Arranged with your mentor after enrolment</span>
                      </div>
                    )}
                    {selectedDate && (
                      <div className="flex justify-between">
                        <span className="text-white/60">Date</span>
                        <span className="text-white">{selectedDate.toLocaleDateString()}</span>
                      </div>
                    )}
                    {selectedSlots.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-white/60">Time</span>
                        <span className="text-white">
                          {THIRTY_MIN_PACKAGES.includes(selectedService?.id)
                            ? `${formatTime12h(selectedSlots[0])} - ${formatTime12h(selectedSlots[0], 30)}`
                            : FORTYFIVE_MIN_PACKAGES.includes(selectedService?.id)
                            ? `${formatTime12h(selectedSlots[0])} - ${formatTime12h(selectedSlots[0], 45)}`
                            : ONE_HOUR_PACKAGES.includes(selectedService?.id)
                            ? formatTimeRange12h(selectedSlots[0], selectedSlots[0] + 1)
                            : NINETY_MIN_PACKAGES.includes(selectedService?.id) 
                            ? formatTimeRange12h(selectedSlots[0], selectedSlots[0] + 1, 30)
                            : formatTimeRange12h(selectedSlots[0], selectedSlots[selectedSlots.length - 1] + 1)
                          }
                        </span>
                      </div>
                    )}
                    {selectedService?.type === "per_song" && (
                      <div className="flex justify-between">
                        <span className="text-white/60">Quantity</span>
                        <span className="text-white">{quantity} song(s)</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-white/60">Name</span>
                      <span className="text-white">{formData.fullName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Email</span>
                      <span className="text-white">{formData.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">Phone</span>
                      <span className="text-white">{formData.phone}</span>
                    </div>
                  </div>
                  <div className="border-t border-dream-border mt-4 pt-4">
                    {/* Promo Code Section */}
                    {!PROMO_EXCLUDED_SERVICES.includes(selectedService?.id) && (
                      <div className="mb-4">
                        <Label className="text-white/60 mb-2 block text-sm">Promo Code (Optional)</Label>
                        <div className="flex gap-2">
                          <Input
                            value={promoCode}
                            onChange={(e) => {
                              setPromoCode(e.target.value.toUpperCase());
                              setPromoValidation(null);
                            }}
                            placeholder="DMRG-XXXXX"
                            className="bg-dream-bg border-dream-border text-white flex-1"
                            data-testid="input-promo-code"
                          />
                          <Button
                            type="button"
                            onClick={validatePromoCode}
                            disabled={validatingPromo || !promoCode.trim()}
                            className="bg-dream-surface border border-dream-border text-white hover:border-dream-violet"
                            data-testid="btn-validate-promo"
                          >
                            {validatingPromo ? "..." : "Apply"}
                          </Button>
                        </div>
                        {promoValidation && (
                          <p className={`text-sm mt-2 ${promoValidation.valid ? "text-green-400" : "text-red-400"}`}>
                            {promoValidation.valid 
                              ? `✓ ${promoValidation.discount_percent}% discount applied!` 
                              : promoValidation.error}
                          </p>
                        )}
                      </div>
                    )}
                    {PROMO_EXCLUDED_SERVICES.includes(selectedService?.id) && (
                      <p className="text-white/40 text-xs mb-4">
                        Promo codes are not available for DJ services or courses.
                      </p>
                    )}
                    <VoucherRedeem serviceId={selectedService?.id} voucher={voucher} setVoucher={setVoucher} />
                    
                    {/* Total Section */}
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-white">
                        {promoValidation?.valid ? "Original Price" : "Total"}
                      </span>
                      <span className={`font-mono text-xl ${promoValidation?.valid ? "text-white/50 line-through" : "text-dream-violet"}`}>
                        ${calculateTotal().toLocaleString('en-AU', {minimumFractionDigits: 2, maximumFractionDigits: 2})} AUD
                      </span>
                    </div>
                    {promoValidation?.valid && (
                      <>
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-green-400 text-sm">Discount ({promoValidation.discount_percent}%)</span>
                          <span className="text-green-400 font-mono">
                            -${(calculateTotal() * (promoValidation.discount_percent / 100)).toFixed(2)} AUD
                          </span>
                        </div>
                        <div className="flex justify-between items-center mt-2 pt-2 border-t border-dream-border">
                          <span className="font-semibold text-white">You Pay</span>
                          <span className="font-mono text-2xl text-dream-violet" data-testid="discounted-total">
                            ${(calculateTotal() * (1 - promoValidation.discount_percent / 100)).toFixed(2)} AUD
                          </span>
                        </div>
                      </>
                    )}
                    {voucher && (() => {
                      const base = promoValidation?.valid ? calculateTotal() * (1 - promoValidation.discount_percent / 100) : calculateTotal();
                      const used = Math.min(voucher.balance, base);
                      return (
                        <>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-amber-300 text-sm">Gift card {voucher.code}</span>
                            <span className="text-amber-300 font-mono" data-testid="booking-voucher-deduction">-${used.toFixed(2)} AUD</span>
                          </div>
                          <div className="flex justify-between items-center mt-2 pt-2 border-t border-dream-border">
                            <span className="font-semibold text-white">Due Today</span>
                            <span className="font-mono text-2xl text-dream-violet" data-testid="booking-voucher-due">${(base - used).toFixed(2)} AUD</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
                <PayLaterCalculator total={Math.max(0, (promoValidation?.valid ? calculateTotal() * (1 - promoValidation.discount_percent / 100) : calculateTotal()) - (voucher?.balance || 0))} />
                <p className="text-white/50 text-sm text-center mb-4">
                  You will be redirected to our secure checkout to complete your payment.
                </p>
              </motion.div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-dream-border">
              {step > 1 ? (
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="border-dream-border text-white hover:border-dream-violet hover:text-dream-violet bg-transparent"
                  data-testid="btn-back"
                >
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  Back
                </Button>
              ) : (
                <div />
              )}
              
              {step < 4 ? (
                <Button
                  onClick={handleNext}
                  className="bg-dream-violet text-white hover:bg-dream-violet-glow"
                  data-testid="btn-next"
                >
                  Next
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-white text-black hover:bg-dream-violet hover:text-white"
                  data-testid="btn-pay"
                >
                  {loading ? "Processing..." : "Pay Now"}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
